/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { handleGenerateMultipleChoiceOptions } from '../../../utils/generateMultipleChoiceOptions'
import generatePassFailCriteria from '../../../utils/generatePassFailCriteria'

import translateText from '@/utils/translateText'
import { BASE_MULTIPLE_CHOICE_FIELD_VALUES, type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { getImageFromService } from '@/utils/generateImageSearchQuery'
import { checkIsTextPromptCard } from '@/views/NewBuilder/utils/check-type-prompt-card'
import generateGradingCriteria from '@/utils/generateGradingCriteria'
import generateCorrectAnswer from '@/utils/generateCorrectAnswer'

export async function handleAsyncUpdateBulkImport(
  card: CardActivityWithId,
  field: keyof CardActivityWithId | 'image.url',
  updates: any,
) {
  const cardUpdated = { ...card, image: { ...card.image } }

  const { text: prompt } = checkIsTextPromptCard(card, true)

  let result = {}

  if (field === 'choices') {
    result = await handleAsyncUpdateChoices(card)
  }
  if (field === 'grading_method') {
    result = await handleAsyncUpdateGradingCriteria(card, {
      grading_method: updates.grading_method,
      performance_expectations: updates.performance_expectations,
      studentLevel: updates.studentLevel,
      gradingCriteriaWithAI: updates.gradingCriteriaWithAI,
      rubricId: updates.rubricId,
      points: updates.points,
      aiFocus: updates.aiFocus,
      target_proficiency_level: updates.target_proficiency_level,
      standardId: updates.standardId,
    })
  }
  if (field === 'native_text') {
    result = await handleAsyncUpdateTranslation(card, {
      language: updates.language,
      defaultLanguage: updates.defaultLanguage,
      generateTranslations: updates.generateTranslations,
    })
  }
  if (field === 'correct_answer') {
    result = await handleAsyncUpdateCorrectAnswer(card)
  }

  if (field === 'image.url') {
    // this moment updates is 'unsplash' or 'google'
    const imageUrl = await getImageFromService(prompt ?? '', updates)

    if (imageUrl) {
      cardUpdated.image.url = imageUrl
    }
  }

  return {
    ...cardUpdated,
    ...result,
  }
}

const handleAsyncUpdateChoices = async (card: CardActivityWithId) => {
  const response = await handleGenerateMultipleChoiceOptions(card.question ?? '', card.MCQType === 'multi')

  if (response.error) {
    return {
      choices: BASE_MULTIPLE_CHOICE_FIELD_VALUES.choices,
      answer: BASE_MULTIPLE_CHOICE_FIELD_VALUES.answer,
    }
  }

  const choices = response?.choices || BASE_MULTIPLE_CHOICE_FIELD_VALUES.choices
  const answer = response?.answers || BASE_MULTIPLE_CHOICE_FIELD_VALUES.answer

  return {
    choices,
    answer,
  }
}

const handleAsyncUpdateGradingCriteria = async (
  card: CardActivityWithId,
  updates: {
    grading_method: CardActivityWithId['grading_method']
    performance_expectations: string
    studentLevel: string
    gradingCriteriaWithAI: boolean
    rubricId: string
    points: number
    aiFocus: 'language' | 'correctness' | 'both'
    target_proficiency_level: string
    standardId: string
  },
) => {
  const isSimple = updates.grading_method === 'simple'
  const newCard = { ...card }

  newCard.grading_method = updates.grading_method || newCard.grading_method
  newCard.grading_criteria = updates.performance_expectations || newCard.grading_criteria
  newCard.rubricId = updates.rubricId || newCard.rubricId
  newCard.points = updates.points || newCard.points
  newCard.target_proficiency_level = updates.target_proficiency_level || newCard.target_proficiency_level
  newCard.standardId = updates.standardId || newCard.standardId
  if (updates.gradingCriteriaWithAI && isSimple) {
    const gradingCriteria = await generateGradingCriteria({
      prompt: card.prompt ?? '',
      level: updates.studentLevel,
      instructions: updates.performance_expectations,
      focus: updates.aiFocus,
    })

    if ('criteria' in gradingCriteria && 'title' in gradingCriteria) {
      newCard.grading_criteria = gradingCriteria.criteria
      newCard.grading_criteria_title = gradingCriteria.title
    }
  }

  return newCard
}

export const handleAsyncUpdateTranslation = async (
  card: CardActivityWithId,
  updates: {
    language: string
    defaultLanguage: string
    generateTranslations: boolean
  },
) => {
  const newCard = { ...card }

  if (updates.generateTranslations) {
    const translatedText = await translateText({
      target_text: card.target_text ?? '',
      toLanguage: updates.defaultLanguage,
      language: updates.language,
    })

    newCard.native_text = translatedText
  }

  return newCard
}

const handleAsyncUpdateCorrectAnswer = async (card: CardActivityWithId) => {
  const response = await generateCorrectAnswer({
    question: card?.prompt || '',
    characterLimit: card?.maxCharacters || 0,
    language: card?.language || 'en',
  })

  if ('success' in response && response.success && 'correct_answer' in response) {
    return {
      correct_answer: response.correct_answer,
    }
  }

  return {}
}
